import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { apiBaseUrl } from "../../lib/env";

/** The half of `AccountProfile` the public pages read. The full shape is generated in the sdk. */
export interface SessionAccount {
  handle: string;
  display_name: string;
  state: string;
}

/** `GET /v1/auth/session`, or null when signed out. A 401 is an answer, never an error.
 *
 * Absolute, through `apiBaseUrl`, and `credentials: "include"` rather than `"same-origin"`.
 * The api answers on its own origin, so a relative path reaches the dashboard host, which
 * forwards nothing to it, and `"same-origin"` would withhold the session cookie from the one
 * host that can read it. The two hosts share a registrable domain, so the `SameSite=Lax`
 * cookie still travels. */
export function useSession() {
  return useQuery({
    queryKey: ["auth", "session"],
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<SessionAccount | null> => {
      const response = await fetch(`${apiBaseUrl}/v1/auth/session`, {
        headers: { accept: "application/json" },
        credentials: "include",
      });
      if (response.status === 401) return null;
      if (!response.ok) return null;
      const body = (await response.json()) as { account?: SessionAccount };
      return body.account ?? null;
    },
  });
}

/** Ends this browser's session. The next request is unauthenticated, with no window in between. */
export function useSignOut(): UseMutationResult<void, Error, void> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await fetch(`${apiBaseUrl}/v1/auth/session`, {
        method: "DELETE",
        credentials: "include",
      });
    },
    onSuccess: () => {
      void client.invalidateQueries();
    },
  });
}
